import store from '@/store/index';
import mixAlert from '@/mixin/alert.mixin';
import navmenu from '@/utils/constants/navMenu.js';
import StudentStatuses from '@/utils/constants/student/studentStatuses';
import { getSysparameterByNames } from '@/modules/system-parameter-managment/api';

const PARAM_MenuAdvanceModeEnabled = 'MenuAdvanceModeEnabled';

/**
 * El parámetro no cambia durante la sesión, por lo que se consulta una sola
 * vez y se reutiliza al navegar entre los menús de los distintos módulos.
 */
let advanceModeRequest = null;

/**
 * Indica si el modo avanzado del menú está habilitado (campo de búsqueda,
 * favoritos y selector de vista lista/tarjetas). Si el parámetro no existe o el servicio
 * falla, el modo avanzado queda deshabilitado.
 *
 * @returns {Promise<boolean>}
 */
export const isMenuAdvanceModeEnabled = () => {
  if (advanceModeRequest == null) {
    advanceModeRequest = getSysparameterByNames({
      parameterNames: [PARAM_MenuAdvanceModeEnabled],
    })
      .then(
        ({ data }) =>
          data
            ?.find((x) => x.name == PARAM_MenuAdvanceModeEnabled)
            ?.staticValue?.toLowerCase() === 'true'
      )
      .catch(() => false);
  }

  return advanceModeRequest;
};

/**
 * Lógica compartida por los menús de módulo (PEI, Estudiante, Libros,
 * Recursos Humanos, Operaciones): modo avanzado y validación del estudiante
 * seleccionado antes de navegar a una opción.
 *
 * Cada menú activa únicamente el comportamiento que le corresponde
 * sobrescribiendo el computed `menuNavigationOptions`:
 *
 * - autoNavigateOnStudentSelected: navega a la ruta pendiente cuando el
 *   usuario selecciona un estudiante (primera vez).
 * - trackViewCard: guarda en el store la tarjeta desde la cual se navegó.
 * - requireRegistration: exige que el estudiante tenga registro en el
 *   programa de Educación Especial.
 * - webServiceSearchRoutes: rutas en las que la búsqueda del estudiante
 *   se habilita únicamente por web service.
 */
export default {
  mixins: [mixAlert],
  data() {
    return {
      menuAdvanceModeEnabled: false,
    };
  },
  computed: {
    menuNavigationOptions() {
      return {};
    },
    navigateToViewDisplay() {
      const student = this.$store.state['$_student'] ?? {};
      return student.navigateToViewDisplay;
    },
  },
  watch: {
    /** Aplica cuando el estudiante no estaba seleccionado (primera vez) */
    navigateToViewDisplay() {
      if (!this.menuNavigationOptions.autoNavigateOnStudentSelected) {
        return;
      }

      const route = this.$store.state.navigateToViewRoute;
      if (this.navigateToViewDisplay === true && route != null && route != '') {
        this.$router.push({ path: route });
      }
    },
  },
  async created() {
    this.menuAdvanceModeEnabled = await isMenuAdvanceModeEnabled();
  },
  mounted() {
    if (store.getters.IsValidSelectedStudent) {
      store.commit('$_student/setShowStudentSelected', true);
    }
  },
  methods: {
    async validateStudent(option) {
      const {
        trackViewCard = false,
        requireRegistration = false,
        webServiceSearchRoutes = [],
      } = this.menuNavigationOptions;

      if (!store.getters.IsValidSelectedStudent && option.needSelectedStudent) {
        store.commit(
          'setWebServiceStudentSearchEnabled',
          webServiceSearchRoutes.includes(option.route)
        );
        store.commit('$_student/toggleSearchMenu');
        store.commit('setNavigationToViewRoute', option.route);

        if (trackViewCard) {
          store.commit('setNavigationToViewCard', option.id);
        }

        store.commit('$_student/showStudentBox', option.needSelectedStudent);
        return false;
      } else if (!option.needSelectedStudent) {
        store.commit('$_student/setShowStudentSelected', false);
      } else {
        store.commit('$_student/setShowStudentSelected', true);
      }

      if (!requireRegistration || !option.needSelectedStudent) {
        return true;
      }

      //El estudiante requiere registro en todos los módulos excepto registro
      const isRegistrationModule = option.id === navmenu.RegistryHistory;
      const selectedUser = store.getters.SelectedStudent;
      const requireUserRegistration = !(
        isRegistrationModule ||
        (selectedUser != null &&
          selectedUser.statusId != null &&
          selectedUser.statusId != StudentStatuses.Disponible)
      );

      store.commit(
        'setSelectedStudentMustHaveRegistration',
        requireUserRegistration
      );

      if (requireUserRegistration) {
        this.ShowToast(
          'Alerta',
          'El estudiante no cuenta con un registro para el programa de Educación Especial.',
          'error'
        );

        return false;
      }

      return true;
    },
  },
};
